import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { motion } from "framer-motion";
import { colors } from "./colors";

const MotionBox = motion(Box);

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.2 },
};

const GLITCH_KEYFRAMES = keyframes`
  0%, 90%, 100% {
    clip-path: none;
    transform: none;
  }
  92% {
    clip-path: polygon(0 10%, 100% 10%, 100% 35%, 0 35%);
    transform: translate(-1px);
  }
  94% {
    clip-path: polygon(0 55%, 100% 55%, 100% 75%, 0 75%);
    transform: translate(1px);
  }
  96% {
    clip-path: none;
    transform: none;
  }
`;

const SECTION_ROOT_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  overflowX: "clip",
  boxSizing: "border-box",
  background: `
    linear-gradient(
      to bottom,
      #050505 0%,
      #080808 100%
    )
  `,
  paddingTop: "4rem",
};

const ATMOSPHERE_SX = {
  position: "absolute",
  inset: 0,
  background: `
    radial-gradient(
      ellipse at top,
      rgba(232,163,61,0.06),
      transparent 60%
    )
  `,
  pointerEvents: "none",
};

const SECTION_INNER_SX = {
  position: "relative",
  zIndex: 2,
  width: "100%",
  maxWidth: "92rem",
  minWidth: 0,
  mx: "auto",
  boxSizing: "border-box",
  px: {
    xs: 2,
    md: 4,
    lg: 5,
  },
};

const GRID_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    lg: "minmax(0, 0.92fr) minmax(0, 1.08fr)",
  },
  gap: {
    xs: "5rem",
    lg: "5.5rem",
  },
  alignItems: "start",
};

const PANEL_SX = {
  display: "flex",
  flexDirection: "column",
  position: "relative",
  zIndex: 1,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const CHAOS_SIDE_SX = {
  ...PANEL_SX,
};

const SOLUTION_SIDE_SX = {
  ...PANEL_SX,
};

const CHAOS_LABEL_SX = {
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
  mb: 1.5,
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: "rgba(192, 57, 43, 0.7)",
  "&::before": {
    content: '""',
    display: "block",
    width: 24,
    height: "1px",
    background: "rgba(192, 57, 43, 0.5)",
  },
};

const SOLUTION_LABEL_SX = {
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
  mb: 1.5,
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: colors.primary.main,
  "&::before": {
    content: '""',
    display: "block",
    width: 24,
    height: "1px",
    background: colors.primary.main,
  },
};

const CHAOS_TITLE_SX = {
  position: "relative",
  zIndex: 1,
  maxWidth: "100%",
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2rem", md: "3rem", lg: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: "rgba(220, 220, 220, 0.85)",
  animation: `${GLITCH_KEYFRAMES} 9s infinite`,
};

const SOLUTION_TITLE_SX = {
  position: "relative",
  zIndex: 1,
  maxWidth: "100%",
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2rem", md: "3rem", lg: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  "& span": {
    background: `
      linear-gradient(
        180deg,
        #f7cb79 0%,
        #f0b458 40%,
        #de8f2c 100%
      )
    `,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
};

const LIST_SX = {
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 1.1,
  mt: 2.5,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
};

const CHAOS_ITEM_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.9,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  px: 1.4,
  py: 1,
  border: "0.5px solid rgba(192, 57, 43, 0.3)",
  position: "relative",
  overflow: "hidden",
  background: `
    linear-gradient(
      180deg,
      rgba(255,255,255,0.018),
      rgba(255,255,255,0.008)
    )
  `,
  backdropFilter: "blur(10px)",
  transition: "all 300ms ease",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
    background: `
      linear-gradient(
        to bottom,
        transparent,
        #C0392B,
        transparent
      )
    `,
    opacity: 0.5,
  },
  "&:hover": {
    borderColor: "rgba(192,57,43,0.34)",
    transform: { xs: "none", md: "translateY(-2px)" },
    background: `
      linear-gradient(
        180deg,
        rgba(255,255,255,0.024),
        rgba(255,255,255,0.012)
      )
    `,
  },
};

const CHAOS_ICON_SX = {
  fontSize: "1.1rem",
  flexShrink: 0,
  mt: 0.15,
  position: "relative",
  zIndex: 1,
  color: "rgba(192, 57, 43, 0.85)",
};

const CHAOS_ITEM_TEXT_SX = {
  position: "relative",
  zIndex: 1,
  flex: 1,
  minWidth: 0,
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "0.82rem",
  fontWeight: 400,
  letterSpacing: "0.02em",
  lineHeight: 1.5,
  color: "rgba(220, 100, 90, 0.85)",
  overflowWrap: "anywhere",
};

const SOLUTION_ITEM_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.9,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  px: 1.4,
  py: 1,
  border: `0.5px solid rgba(232, 163, 61, 0.3)`,
  position: "relative",
  overflow: "hidden",
  background: `
    linear-gradient(
      180deg,
      rgba(255,255,255,0.02),
      rgba(255,255,255,0.012)
    )
  `,
  backdropFilter: "blur(10px)",
  transition: "all 300ms ease",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(232,163,61,0.03),
        transparent
      )
    `,
    opacity: 0,
    transition: "opacity 300ms ease",
    pointerEvents: "none",
  },
  "&:hover": {
    transform: { xs: "none", md: "translateY(-2px)" },
    borderColor: "rgba(232,163,61,0.44)",
    boxShadow: `
      0 0 0 1px rgba(232,163,61,0.06),
      0 12px 30px rgba(0,0,0,0.24)
    `,
  },
  "&:hover::after": {
    opacity: 1,
  },
};

const SOLUTION_ICON_SX = {
  fontSize: "1.1rem",
  flexShrink: 0,
  mt: 0.15,
  position: "relative",
  zIndex: 1,
  color: colors.primary.main,
};

const SOLUTION_ITEM_TEXT_SX = {
  position: "relative",
  zIndex: 1,
  flex: 1,
  minWidth: 0,
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "0.82rem",
  fontWeight: 400,
  lineHeight: 1.5,
  color: colors.text.secondary,
  overflowWrap: "anywhere",
};

const CHAOS_ITEMS = [
  {
    key: "cheaters",
    text: "Banned cheaters back in 10 minutes on a new Steam account",
    icon: ReportProblemOutlinedIcon,
  },
  {
    key: "smurfs",
    text: "2500 ELO smurfs destroying low-rank lobbies",
    icon: ReportProblemOutlinedIcon,
  },
  {
    key: "throws",
    text: "Intentional throws and rage quits with no real consequences",
    icon: ReportProblemOutlinedIcon,
  },
  {
    key: "reports",
    text: "Mass false reports used as a griefing tool",
    icon: ReportProblemOutlinedIcon,
  },
  {
    key: "anticheat",
    text: "Anti-cheat blind to visual cheats the server can see",
    icon: ReportProblemOutlinedIcon,
  },
];

const SOLUTION_ITEMS = [
  {
    key: "kyc",
    text: "Phone + biometric KYC — one identity, one account, permanent bans",
    icon: VerifiedOutlinedIcon,
  },
  {
    key: "smurf",
    text: "Smurfing is structurally impossible. Not just discouraged",
    icon: ShieldOutlinedIcon,
  },
  {
    key: "telemetry",
    text: "Server-side telemetry flags griefing and throws automatically",
    icon: SensorsOutlinedIcon,
  },
  {
    key: "reports",
    text: "Report quality is tracked — abuse lowers your own Trust Score",
    icon: GppGoodOutlinedIcon,
  },
  {
    key: "verdict",
    text: "Tick-by-tick behavioral analysis with a public CLEAN / WATCH verdict",
    icon: VisibilityOutlinedIcon,
  },
];

function ComparedList({ items, itemSx, textSx, iconSx, stagger = 0 }) {
  return (
    <Box sx={LIST_SX}>
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <MotionBox
            key={item.key}
            {...REVEAL}
            transition={{
              duration: 0.9,
              delay: stagger + index * 0.08,
              ease: [0.22, 1, 0.36, 1],
            }}
            sx={itemSx}
          >
            <Icon sx={iconSx} />
            <Typography component="p" sx={textSx}>
              {item.text}
            </Typography>
          </MotionBox>
        );
      })}
    </Box>
  );
}

export default function OuroCompared() {
  return (
    <Box component="section" id="problem" sx={SECTION_ROOT_SX}>
      <Box sx={ATMOSPHERE_SX} />

      <Box sx={SECTION_INNER_SX}>
        <MotionBox
          {...REVEAL}
          transition={{
            duration: 1,
            ease: [0.22, 1, 0.36, 1],
          }}
          sx={{
            textAlign: "center",
            mb: {
              xs: "4rem",
              md: "6rem",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: "1px",
                background: colors.primary.hover,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                color: colors.primary.main,
              }}
            >
              COMPETITIVE INFRASTRUCTURE
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: {
                xs: "2.6rem",
                md: "3.75rem",
              },
              lineHeight: 0.92,
              letterSpacing: "-0.07em",
              fontWeight: 700,
              color: "#fff",
            }}
          >
            Competitive CS has
            <br />
            a structural problem.
          </Typography>

          <Typography
            sx={{
              mt: 3,
              maxWidth: "42rem",
              mx: "auto",
              fontSize: "1rem",
              lineHeight: 1.8,
              color: "rgba(255,255,255,0.64)",
            }}
          >
            Most platforms try to moderate chaos.
            Ouro removes the incentives that create it.
          </Typography>
        </MotionBox>

        <Box sx={GRID_SX}>
          <MotionBox
            {...REVEAL}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            sx={CHAOS_SIDE_SX}
          >
            <Typography component="div" sx={CHAOS_LABEL_SX}>
              CS2 Premier / FACEIT
            </Typography>
            <Typography component="h2" sx={CHAOS_TITLE_SX}>
              What you
              <br />
              Actually get
            </Typography>
            <ComparedList
              items={CHAOS_ITEMS}
              itemSx={CHAOS_ITEM_SX}
              textSx={CHAOS_ITEM_TEXT_SX}
              iconSx={CHAOS_ICON_SX}
              stagger={0.12}
            />
          </MotionBox>

          <MotionBox
            {...REVEAL}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            sx={SOLUTION_SIDE_SX}
          >
            <Typography component="div" sx={SOLUTION_LABEL_SX}>
              Ouro Ecosystem
            </Typography>
            <Typography component="h2" sx={SOLUTION_TITLE_SX}>
              By
              <br />
              <span>Design</span>
            </Typography>
            <ComparedList
              items={SOLUTION_ITEMS}
              itemSx={SOLUTION_ITEM_SX}
              textSx={SOLUTION_ITEM_TEXT_SX}
              iconSx={SOLUTION_ICON_SX}
              stagger={0.22}
            />
          </MotionBox>
        </Box>
      </Box>
    </Box>
  );
}
